// =============================================================================
// client/src/components/CallSheetTab.jsx | v1.0 | SQ-S1 | Profile → Call Sheet tab
// =============================================================================
// The recruit's own task list, mounted from RecruitEdit.jsx beside OutreachTab
// (the mount idiom is copied; OutreachTab itself is S99's file and untouched).
//
// Rows: service (seq of total) · DateTriad(start) · DateTriad(deadline) — each
// PATCHes on change and toasts · status badge · "Task Completed" → modal →
// POST complete · "Un-complete" on completed rows. Completed rows also show
// completed_on, who, and notes.
//
// Bottom: "Add service" (cw_004 §B) — dropdown of active services (existing
// GET /api/services, sorted by sort_order), start + deadline triads, Add →
// POST /api/call-sheet/recruits/:id/services → refetch. Quantity is always 1.
//
// The client never enforces authorization. Controls render for every role that
// can open the tab; a server 403 is rendered verbatim in the toast.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import DateTriad from './DateTriad.jsx';
import CompleteTaskModal from './CompleteTaskModal.jsx';
import ConfirmModal from './ConfirmModal.jsx';
import Toast from './Toast.jsx';
import {
  CALL_SHEET_STATUS_COLORS, displayStatus, formatDateUTC, seqLabel,
  completedByName, errorMessage, todayLocalISO, taskTitle, taskParent,
} from '../lib/callSheet.js';

export default function CallSheetTab({ recruitId, me }) {
  const [tasks, setTasks] = useState([]);
  const [today, setToday] = useState(todayLocalISO());
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [completing, setCompleting] = useState(null);   // the task in the modal
  const [cancelling, setCancelling] = useState(null);   // { deliverableId, serviceName, count } in the confirm

  const load = useCallback(async () => {
    try {
      const data = await api.get(`/api/call-sheet/recruits/${recruitId}/tasks`);
      setTasks(data.tasks || []);
      if (data.today) setToday(data.today);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the Call Sheet'));
    } finally {
      setLoading(false);
    }
  }, [recruitId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/api/services')
      .then(({ services: list }) => {
        const active = (list || []).filter((s) => s.is_active !== false);
        active.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name));
        setServices(active);
      })
      .catch(() => setServices([]));
  }, []);

  const role = me?.team?.role || null;
  const canUncomplete = ['admin', 'manager', 'recruiting_coordinator'].includes(role);
  // cw_006: cancel-remaining is admin/manager only. The server refuses an RC
  // regardless; hiding it here just avoids offering a button that 403s.
  const canCancel = ['admin', 'manager'].includes(role);

  async function cancelRemaining() {
    if (!cancelling) return;
    setBusyId(cancelling.deliverableId);
    try {
      const r = await api.post(`/api/call-sheet/deliverables/${cancelling.deliverableId}/cancel-remaining`, {});
      setCancelling(null);
      setToast({ tone: 'info', message: `${r.cancelled} task${r.cancelled === 1 ? '' : 's'} cancelled` });
      await load();
    } catch (err) {
      setToast({ tone: 'error', message: errorMessage(err, 'Could not cancel the remaining tasks') });
    } finally {
      setBusyId(null);
    }
  }

  async function patchDates(task, patch) {
    setBusyId(task.id);
    try {
      const { task: updated } = await api.patch(`/api/call-sheet/tasks/${task.id}/dates`, patch);
      setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
      setToast({ tone: 'success', message: 'Dates saved' });
    } catch (err) {
      setToast({ tone: 'error', message: errorMessage(err, 'Could not save dates') });
      await load();   // put the row back to what the server holds
    } finally {
      setBusyId(null);
    }
  }

  async function complete({ completed_on, notes }) {
    if (!completing) return;
    setBusyId(completing.id);
    try {
      await api.post(`/api/call-sheet/tasks/${completing.id}/complete`, { completed_on, notes });
      setCompleting(null);
      setToast({ tone: 'success', message: 'Task marked completed' });
      await load();
    } catch (err) {
      setToast({ tone: 'error', message: errorMessage(err, 'Could not complete the task') });
    } finally {
      setBusyId(null);
    }
  }

  async function uncomplete(task) {
    setBusyId(task.id);
    try {
      await api.post(`/api/call-sheet/tasks/${task.id}/uncomplete`, {});
      setToast({ tone: 'info', message: 'Task reopened' });
      await load();
    } catch (err) {
      setToast({ tone: 'error', message: errorMessage(err, 'Could not reopen the task') });
    } finally {
      setBusyId(null);
    }
  }

  const pending = useMemo(() => tasks.filter((t) => t.status === 'pending'), [tasks]);
  const done = useMemo(() => tasks.filter((t) => t.status !== 'pending'), [tasks]);

  // The "Cancel remaining" affordance rides the FIRST pending row of each
  // deliverable (service group), with the count of pending rows it would hit.
  const cancelAnchor = useMemo(() => {
    const byDeliverable = new Map();
    for (const t of pending) {
      const cur = byDeliverable.get(t.recruit_deliverable_id);
      if (!cur) byDeliverable.set(t.recruit_deliverable_id, { firstId: t.id, count: 1, serviceName: t.service_name });
      else cur.count += 1;
    }
    const out = new Map();
    for (const [deliverableId, v] of byDeliverable) out.set(v.firstId, { deliverableId, ...v });
    return out;
  }, [pending]);

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-3">
          Call Sheet
        </h3>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4 mb-4">{error}</div>
        )}

        {loading ? (
          <div className="text-sm text-silver-mute py-8 text-center">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="text-sm text-silver-mute py-8 text-center">No service tasks yet.</div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-[0.65rem] font-bold uppercase tracking-wide text-silver-mute">
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">Deadline</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {[...pending, ...done].map((t) => {
                  const status = displayStatus(t, today);
                  const busy = busyId === t.id || busyId === t.recruit_deliverable_id;
                  const seq = seqLabel(t);
                  const by = completedByName(t);
                  const struck = t.status === 'cancelled';
                  const anchor = canCancel ? cancelAnchor.get(t.id) : null;
                  return (
                    <tr key={t.id} className={`border-b border-gray-100 last:border-0 align-top ${status === 'overdue' ? 'bg-red-50/60' : ''} ${struck ? 'text-silver-mute' : ''}`}>
                      <td className={`px-4 py-3 font-medium ${struck ? 'line-through text-silver-mute' : 'text-silver-text'}`}>
                        {taskTitle(t)}
                        {seq && <span className="ml-2 text-xs font-normal text-silver-mute">{seq}</span>}
                        {taskParent(t) && <div className="text-xs font-normal text-silver-mute">{taskParent(t)}</div>}
                        {anchor && (
                          <div className="mt-1">
                            <button
                              type="button"
                              className="text-xs font-normal text-red-700 underline hover:text-red-900 disabled:opacity-40"
                              disabled={busy}
                              onClick={() => setCancelling(anchor)}
                            >
                              Cancel remaining ({anchor.count})
                            </button>
                          </div>
                        )}
                        {t.status === 'completed' && (
                          <div className="mt-1 text-xs font-normal text-silver-mute">
                            Completed {formatDateUTC(t.completed_on)}{by ? ` by ${by}` : ''}
                            {t.completion_notes && (
                              <div className="whitespace-pre-line mt-0.5 text-silver-text/80">{t.completion_notes}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {t.status === 'pending'
                          ? <DateTriad value={t.start_date} disabled={busy} ariaLabel="Start" onChange={(iso) => patchDates(t, { start_date: iso })} />
                          : <span className={struck ? 'line-through' : 'text-silver-text'}>{formatDateUTC(t.start_date)}</span>}
                      </td>
                      <td className="px-4 py-3">
                        {t.status === 'pending'
                          ? <DateTriad value={t.deadline_date} disabled={busy} ariaLabel="Deadline" onChange={(iso) => patchDates(t, { deadline_date: iso })} />
                          : <span className={struck ? 'line-through' : 'text-silver-text'}>{formatDateUTC(t.deadline_date)}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[0.6rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${CALL_SHEET_STATUS_COLORS[status] || 'bg-gray-100 text-silver-text'}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {t.status === 'pending' && (
                          <button type="button" className="btn-primary text-xs py-1 px-3" disabled={busy} onClick={() => setCompleting(t)}>
                            Task Completed
                          </button>
                        )}
                        {t.status === 'completed' && canUncomplete && (
                          <button type="button" className="btn-secondary text-xs py-1 px-3" disabled={busy} onClick={() => uncomplete(t)}>
                            Un-complete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AddService recruitId={recruitId} services={services} today={today} onAdded={load} onToast={setToast} />

      <CompleteTaskModal
        open={!!completing}
        task={completing}
        today={today}
        busy={!!completing && busyId === completing.id}
        onConfirm={complete}
        onCancel={() => setCompleting(null)}
      />
      <ConfirmModal
        open={!!cancelling}
        title="Cancel remaining tasks?"
        body={cancelling ? `${cancelling.count} pending task${cancelling.count === 1 ? '' : 's'} for ${cancelling.serviceName} will be marked cancelled. Completed tasks are not touched. There is no un-cancel — use Add service to schedule it again.` : ''}
        confirmLabel="Cancel remaining"
        cancelLabel="Keep them"
        destructive
        onConfirm={cancelRemaining}
        onCancel={() => setCancelling(null)}
      />
      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}

// ─── Add service (cw_004 §B) ─────────────────────────────────────────────────

function AddService({ recruitId, services, today, onAdded, onToast }) {
  const [serviceId, setServiceId] = useState('');
  const [start, setStart] = useState('');
  const [deadline, setDeadline] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!start && today) setStart(today); }, [today, start]);

  const ready = !!serviceId && !!start && !!deadline && deadline >= start && !busy;

  async function add() {
    setBusy(true);
    try {
      await api.post(`/api/call-sheet/recruits/${recruitId}/services`, {
        service_id: serviceId, start_date: start, deadline_date: deadline,
      });
      onToast({ tone: 'success', message: 'Service added to the Call Sheet' });
      setServiceId(''); setDeadline('');
      await onAdded();
    } catch (err) {
      onToast({ tone: 'error', message: errorMessage(err, 'Could not add the service') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
      <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-3">
        Add service
      </h3>
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[16rem] flex-1">
          <label className="label-dark mb-1 block">Service</label>
          <select className="input" value={serviceId} onChange={(e) => setServiceId(e.target.value)} disabled={busy}>
            <option value="">Choose a service…</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-dark mb-1 block">Start</label>
          <DateTriad value={start} onChange={setStart} disabled={busy} ariaLabel="Start" />
        </div>
        <div>
          <label className="label-dark mb-1 block">Deadline</label>
          <DateTriad value={deadline} onChange={setDeadline} disabled={busy} ariaLabel="Deadline" />
        </div>
        <button type="button" className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" disabled={!ready} onClick={add}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
      {deadline && start && deadline < start && (
        <p className="text-xs text-red-700 mt-2">Deadline must be on or after the start date.</p>
      )}
      <p className="text-xs text-silver-mute mt-2">Every add is one task. Manual adds are tracked at $0 — they are not a sale.</p>
    </section>
  );
}
